import { computed, Ref } from 'vue';
import { clamp } from 'lodash-es';

import { useCalculations } from '@/lib/calculationstate';
import { PayoutCategory } from '@/lib/payout';
import calculationParams from '@/lib/calculationparams';

import {
  calculateIVAAmount,
  calculateLAUAmount,
  calculateLGUAmount,
  calculateVVUAmount,
} from '@/lib/calculations/uwv';
import {
  calculateWBOAmount,
  calculateWEBAmount,
  calculateWETAmount,
  calculateWEXAmount,
  calculateWEXLGUAmount,
  calculateWHAAmount,
} from '@/lib/calculations/nvschade';
import { deref } from '@/lib/pdf/helpers';

export interface BarGraph {
  name: string | Ref<string>;
  description: string[] | Ref<string[]>;
  segments: BarGraphSegment[];
}

export interface BarGraphSegment {
  value: Ref<number>;
  category: PayoutCategory;
}

function toMonthlySegment(segment: BarGraphSegment): BarGraphSegment {
  return {
    value: computed(() => Math.round(segment.value.value / 12)),
    category: segment.category,
  };
}

export function toMonthlyGraph(graph: BarGraph) {
  return {
    ...graph,
    segments: graph.segments.map(toMonthlySegment),
  };
}

export function halfYearGraph(): BarGraph {
  const { state } = useCalculations();

  return {
    name: 'Eerste half jaar vanaf uw eerste ziektedag',
    description: [
      'De eerste twee jaren dat u niet (volledig) kan werken noemen we ziekte.',
      'Het eerste half jaar ontvangt u 100% van uw loon van uw werkgever. Dit is zo afgesproken in de CAO.',
    ],
    segments: [
      { value: computed(() => state.salary), category: PayoutCategory.Salary },
    ],
  };
}

export function twoYearsGraph(): BarGraph {
  const { state } = useCalculations();

  return {
    name: 'Tot twee jaar vanaf uw eerste ziektedag',
    description: [
      'De volgende 18 maanden ontvangt u nog 90% van uw loon van uw werkgever. '
      + 'Dit is zo afgesproken in de CAO.',
    ],
    segments: [
      {
        value: computed(() => calculationParams.initialPayoutRate * state.salary),
        category: PayoutCategory.ReducedSalary,
      },
    ],
  };
}

export function uwvInitialGraph(): BarGraph {
  const { state, hasHighDisability } = useCalculations();
  const lguPayoutTime = computed(
    () => clamp(state.age - 18, calculationParams.uwv.lguMinPayoutDuration, calculationParams.uwv.lguMaxPayoutDuration),
  );

  const wboAmount = computed(() => (state.insurance.hasWIAEmployee ? calculateWBOAmount(state) : 0));
  const webAmount = computed(() => (state.insurance.hasWIAExcedent ? calculateWEBAmount(state) : 0));
  // TODO: I should probably fold WEX for IVA into WEXLGU as well
  const wexAmount = computed(() => {
    if (!state.insurance.hasWIAExcedent) return 0;
    if (hasHighDisability.value && state.cannotRecover) {
      // WEX bij IVA
      return calculateWEXAmount(state);
    }
    return calculateWEXLGUAmount(state);
  });
  const wetAmount = computed(() => (state.insurance.hasWIAExcedentTop ? calculateWETAmount(state) : 0));

  const descriptionLGU = ['Na twee jaar ontvangt u een WGA-loongerelateerde uitkering.'];
  const descriptionWEB = [];

  return {
    name: computed(() => (state.disabilityRatio < 0.35
      ? 'Tot 7,5 jaar na uw WIA keuring'
      : `Tot ${lguPayoutTime.value} maanden na uw UWV-keuring`)),
    description: computed(() => (state.disabilityRatio < 0.35 ? descriptionWEB : descriptionLGU)),

    segments: [
      /** 'Always active' */
      { value: computed(() => state.extraIncome), category: PayoutCategory.ExtraIncome },
      /* When high disability */
      {
        value: computed(() => calculateIVAAmount(state)),
        category: PayoutCategory.IVA,
      },
      /* When under LGU ('normal' disability) */
      {
        value: computed(() => calculateLGUAmount(state)),
        category: PayoutCategory.LGU,
      },
      { value: wexAmount, category: PayoutCategory.WEX },
      { value: wetAmount, category: PayoutCategory.WET },
      /* When limited disability */
      { value: wboAmount, category: PayoutCategory.WBO },
      { value: webAmount, category: PayoutCategory.WEB },
    ],
  };
}

export function uwvFinalGraph(): BarGraph {
  const { state } = useCalculations();

  const whaAmount = computed(() => (state.insurance.hasWIAEmployee ? calculateWHAAmount(state) : 0));
  const wexAmount = computed(() => (state.insurance.hasWIAExcedent ? calculateWEXAmount(state) : 0));
  const wetAmount = computed(() => (state.insurance.hasWIAExcedentTop ? calculateWETAmount(state) : 0));

  return {
    name: 'Tot uw AOW-leeftijd',
    description: [],
    segments: [
      { value: computed(() => state.extraIncome), category: PayoutCategory.ExtraIncome },
      /* When high disability */
      {
        value: computed(() => calculateIVAAmount(state)),
        category: PayoutCategory.IVA,
      },
      {
        value: computed(() => calculateVVUAmount(state)),
        category: PayoutCategory.VVU,
      },
      {
        value: computed(() => calculateLAUAmount(state)),
        category: PayoutCategory.LAU,
      },
      { value: whaAmount, category: PayoutCategory.WHA },
      { value: wexAmount, category: PayoutCategory.WEX },
      { value: wetAmount, category: PayoutCategory.WET },
    ],
  };
}

/**
 * Returns the list of graphs containing all pertinent calculations.
 */
export function useYearlyGraphs(): BarGraph[] {
  return [
    halfYearGraph(),
    twoYearsGraph(),
    uwvInitialGraph(),
    uwvFinalGraph(),
  ];
}

/**
 * Returns the list of graphs, converted to monthly graphs
 */
export function useGraphs(): BarGraph[] {
  return useYearlyGraphs().map(toMonthlyGraph);
}

/**
 * Returns a list of all unique payout categories in use by the set of graphs given.
 *
 * @param graphs
 */
export function listAllPayoutCategories(graphs: BarGraph[]): PayoutCategory[] {
  const results: PayoutCategory[] = [];

  graphs.forEach((graph) => {
    graph.segments.forEach((segment) => {
      if (
        deref(segment.value) > 0
        && !results.includes(segment.category)
        && segment.category !== PayoutCategory.ReducedSalary
      ) {
        results.push(segment.category);
      }
    });
  });

  return results.sort((a, b) => a - b);
}
